import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Sidebar, Header, BottomNav } from '@/shared/components';
import { CustomModal, CustomButton } from '@/shared/components/atoms';
import { useAuthStore } from '@/shared/stores/authStore';
import { useToastStore } from '@/shared/stores/toastStore';
import { faSignOutAlt, faTimes } from '@fortawesome/free-solid-svg-icons';
import './MainLayout.css';

/**
 * Main Application Layout
 * Implements a responsive, fixed layout with independent scrolling areas.
 */
export const MainLayout: React.FC = () => {
    const navigate = useNavigate();
    const logoutStore = useAuthStore(state => state.logout);
    const { showToast } = useToastStore();
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const handleLogoutClick = () => {
        setIsLogoutModalOpen(true);
    };

    const confirmLogout = async () => {
        setIsLoggingOut(true);
        // Save slug before we clear it during logout
        const currentUser = useAuthStore.getState().user;
        const isUserSuperAdmin = currentUser?.role === 'SUPER_ADMIN';
        const tenantSlug = isUserSuperAdmin ? null : useAuthStore.getState().tenant?.slug;
        
        // Client-side only logout — no API call
        logoutStore();
        showToast('Logged out successfully', 'success', 'Logout');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        setIsLogoutModalOpen(false);
        setIsLoggingOut(false);
        
        if (tenantSlug) {
            window.location.href = `/login?school_slug=${tenantSlug}`;
        } else {
            window.location.href = '/login';
        }
    };

    return (
        <div className="app-layout">
            <div className="app-layout__container">
                <Sidebar onLogout={handleLogoutClick} />

                <div className="app-layout__main">
                    <Header />

                    <main className="app-layout__content">
                        <Outlet />
                    </main>
                </div>
            </div>

            {/* Logout Confirmation Modal */}
            <CustomModal
                isOpen={isLogoutModalOpen}
                onClose={() => setIsLogoutModalOpen(false)}
                title="Confirm Logout"
                size="small"
                footer={
                    <>
                        <CustomButton 
                            label="Cancel" 
                            variant="outline" 
                            onClick={() => setIsLogoutModalOpen(false)} 
                            disabled={isLoggingOut}
                            faIcon={faTimes}
                        />
                        <CustomButton 
                            label="Logout" 
                            color="danger" 
                            onClick={confirmLogout} 
                            loading={isLoggingOut}
                            faIcon={faSignOutAlt}
                        />
                    </>
                }
            >
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>
                    Are you sure you want to log out? You will need to enter your credentials again to access the portal.
                </p>
            </CustomModal>

            {/* Bottom Tab Bar - Visible on Mobile/Tablet (< lg) */}
            <div className="app-layout__mobile-nav">
                <BottomNav />
            </div>
        </div>
    );
};
